<template>
  <f7-page name="informes-pendientes" class="ipp-page" @page:beforeremove="cancelarTopes">
    <f7-navbar title="Informes pendientes (TPI) por inspector" :back-link="$t('atras')"></f7-navbar>
    <!-- #16 · Este panel NO agrega en la peticion: lee el materializado informes_pendientes, que el
         backend mantiene por eventos + reconcile nocturno. Su fecha de publicacion viene en cada
         respuesta (actualizadoEn), asi que es esa la que se enseña y no un "en vivo" que taparia un
         materializado retrasado. Si no llega, el sello se degrada solo a "no se sabe". -->
    <div class="sello-datos-bar"><sello-datos estado="fecha" :fecha="actualizado" /></div>

    <div class="ipp">
      <div class="hero">
        <span class="tag">📄 Planificación · Situación de informes pendientes (TPI)</span>
        <h1>Informes pendientes por inspector</h1>
        <div class="sub">Pendientes de producir (TPI No) · importe, antigüedad y materia · acceso desde el calendario</div>
        <div class="fresh" v-if="actualizado">🔄 Dato pre-agregado y siempre fresco · última actualización: {{ fechaHora(actualizado) }}</div>
      </div>

      <!-- Este panel y la vista INF.PEND. de Control de cifras OEB — Planificación miden el mismo
           trabajo pendiente pero responden a preguntas distintas, así que dan cifras distintas.
           Se dice aquí para que nadie las compare a ciegas y lo tome por un descuadre (#14). -->
      <div class="nota-importe">
        <b>Qué importe suma este panel:</b> el <b>importe completo de la línea</b>, que es el dinero parado
        sin emitir de toda la empresa. La vista <b>INF.PEND.</b> de <i>Control de cifras OEB — Planificación</i>
        mide otra cosa —lo que lleva encima cada persona— y suma el <b>IMPORTE REPARTO</b>, la parte imputada
        a cada una. En las líneas repartidas entre varias personas su cifra sale menor, y por eso los dos
        totales no tienen por qué cuadrar. Las dos son correctas.
      </div>

      <div class="banner" v-if="flagOff">
        ⚠️ La funcionalidad está <b>desactivada</b> (flag <code>planificacion.informes-pendientes.enabled=false</code>).
        El panel se llenará cuando se active y se materialicen los datos.
      </div>

      <div class="sect">Todos los inspectores · ordenados por importe pendiente (mayor primero) · pulsa para filtrar</div>
      <!-- #331 · Las tres situaciones se ven distintas: se está cargando, no se ha podido cargar, o
           esto es lo que hay. Con el 504 de la pasarela, esta rejilla se quedaba en ceros —"Total
           TPI(No) 0 €", "0 inspectores"— afirmando un recuento que nadie había hecho. -->
      <div class="ipestado" v-if="cargando.rejilla">
        <f7-preloader></f7-preloader><span>Cargando los inspectores…</span>
      </div>
      <div class="ipfallo" v-else-if="error.rejilla">
        <div class="t">⚠️ No se han podido cargar los inspectores</div>
        <div class="m">{{ error.rejilla }}</div>
        <div class="m">No se enseña ningún total porque no se ha podido contar nada. Vuelve a intentarlo y, si sigue sin cargar, avisa a soporte.</div>
        <button class="ipbtn" @click="cargarRejilla()">Reintentar</button>
      </div>
      <div class="grid" v-else>
        <div class="ipchip total" :class="{ sel: sel === '__TODOS__' }" @click="seleccionar('__TODOS__')">
          <div class="n">Total TPI(No)</div>
          <div class="e">{{ eur(rejilla.totalImporte) }}</div>
          <div class="m">{{ rejilla.totalInformes }} informes · {{ rejilla.numInspectores }} inspectores · {{ rejilla.totalLineasDistintas }} líneas</div>
        </div>
        <div v-for="ins in rejilla.inspectores" :key="ins.inspector" class="ipchip"
             :class="{ sel: sel === ins.inspector }" @click="seleccionar(ins.inspector)">
          <div class="n">{{ ins.inspector }}</div>
          <div class="e">{{ eur(ins.importe) }}</div>
          <div class="m">{{ ins.nInformes }} inf. · +{{ ins.mesesAntiguedadMax || 0 }} m</div>
        </div>
      </div>

      <!-- #331 · Los KPIs, las dos gráficas y el listado salen todos de la MISMA petición (el
           detalle), así que comparten estado: mientras no haya contestado no se pintan sus ceros ni
           sus rótulos de vacío ("Sin informes pendientes.", "Sin materias pendientes."), que es lo
           que la pantalla afirmaba sin haberlo comprobado mientras esperaba al 504. Falla por su
           cuenta: lo que haya traído la rejilla sigue en pantalla. -->
      <div class="ipestado" v-if="cargando.detalle">
        <f7-preloader></f7-preloader><span>Cargando los informes pendientes{{ sel === '__TODOS__' ? '' : ' de ' + sel }}…</span>
      </div>
      <div class="ipfallo" v-else-if="error.detalle">
        <div class="t">⚠️ No se han podido cargar los informes pendientes{{ sel === '__TODOS__' ? '' : ' de ' + sel }}</div>
        <div class="m">{{ error.detalle }}</div>
        <div class="m">No se enseña ninguna cifra porque no se ha podido contar nada. Vuelve a intentarlo y, si sigue sin cargar, avisa a soporte.</div>
        <button class="ipbtn" @click="cargarDetalle(sel)">Reintentar</button>
      </div>
      <template v-else>
      <div class="kpis">
        <div class="kpi"><div class="lab">Informes pendientes</div><div class="val">{{ kpis.nInformes }}</div><div class="ksub">TPI(No) · TPC(Sí)</div></div>
        <div class="kpi"><div class="lab">Importe pendiente</div><div class="val">{{ eur(kpis.importe) }}</div><div class="ksub">≈ {{ eur(kpis.importeMedio) }} / informe</div></div>
        <div class="kpi alert"><div class="lab">Más antiguo</div><div class="val">{{ kpis.masAntiguo ? fecha(kpis.masAntiguo) : '—' }}</div><div class="ksub">antigüedad +{{ kpis.mesesAntiguedad || 0 }} meses</div></div>
        <div class="kpi"><div class="lab">&gt; 3 meses</div><div class="val">{{ eur(kpis.importeMas3Meses) }}</div><div class="ksub">{{ kpis.nMas3Meses }} informes · prioridad</div></div>
      </div>

      <div class="row2">
        <div class="ipcard">
          <h3>Importe pendiente por mes de antigüedad{{ sel === '__TODOS__' ? '' : ' · ' + sel }}</h3>
          <div class="cs">24 meses + bucket "&gt; 2 años" (izquierda, alarma) que recoge el backlog viejo.</div>
          <div class="plot">
            <div v-for="(b, i) in antiguedad" :key="i" class="ipcol" :class="{ over2y: b.masDeDosAnios }"
                 v-tooltip="b.label + ' · ' + eur(b.importe) + ' · ' + b.n + ' inf.'">
              <div class="f" :style="{ height: barH(b.importe) + '%' }"></div>
            </div>
          </div>
          <div class="xlab">
            <div v-for="(b, i) in antiguedad" :key="'x' + i">{{ etiquetaX(b, i) }}</div>
          </div>
        </div>
        <div class="ipcard">
          <h3>Importe pendiente por materia</h3>
          <div class="cs">Todas las materias con informe pendiente</div>
          <div class="mrow" v-for="m in materias" :key="m.materia">
            <div class="ml"><span class="swatch" :style="{ background: colorMateria(m.materia) }"></span>{{ m.materia }}</div>
            <div class="mtrack"><div class="mfill" :style="{ width: anchoMateria(m.importe) + '%', background: colorMateria(m.materia) }"></div></div>
            <div class="mv">{{ eur(m.importe) }} <small>· {{ m.n }}</small></div>
          </div>
          <div v-if="!materias.length" class="vacio">Sin materias pendientes.</div>
        </div>
      </div>

      <div class="ipcard">
        <div class="cardhead">
          <div>
            <h3>Listado de informes pendientes · {{ sel === '__TODOS__' ? 'todos' : sel }}</h3>
            <div class="cs">Ordenado por antigüedad (más antiguos primero) · {{ items.length }} informes</div>
          </div>
          <button class="expbtn" :disabled="!items.length" @click="exportarExcel()">⬇ Exportar a Excel</button>
        </div>
        <div class="tblwrap">
        <table class="tbl">
          <thead><tr>
            <th>Pedido / línea</th><th>Cliente</th><th>Materia</th><th>Inspector</th>
            <th>Fecha insp.</th><th>Antigüedad</th><th class="tr">Importe</th><th></th>
          </tr></thead>
          <tbody>
            <tr v-for="(it, i) in items" :key="i">
              <td class="ped">{{ it.pedidoNumero }} / {{ it.codigoLinea }}</td>
              <td class="txt">{{ it.cliente || '—' }}</td>
              <td><span class="mtag" :style="{ background: colorMateria(it.materia) + '22', color: colorMateria(it.materia) }">{{ it.materia || '—' }}</span></td>
              <td><span class="itag">{{ it.inspector }}</span></td>
              <td>{{ it.fechaInspeccion ? fecha(it.fechaInspeccion) : '—' }}</td>
              <td class="age" :class="claseEdad(it.dias)">{{ it.dias }} días</td>
              <td class="tr b">{{ eur(it.importe) }}</td>
              <td><a class="goped" @click="irAlPedido(it.pedidoId)" v-if="it.pedidoId">Ir al pedido ↗</a></td>
            </tr>
            <tr v-if="!items.length"><td colspan="8" class="vacio">Sin informes pendientes.</td></tr>
          </tbody>
        </table>
        </div>
        <div class="listmore" v-if="items.length">{{ items.length }} informes · Total {{ eur(kpis.importe) }}</div>
      </div>
      </template>
    </div>
  </f7-page>
</template>

<script>
import InformesPendientes from '../../js/db/InformesPendientes.js';

import SelloDatos from '../../components/SelloDatos.vue';
import carrilPeticiones from '../../js/utils/carrilPeticiones.js';
// El tope de tiempo de una consulta ya está escrito y con dueño (lo usan las barreras de ruta):
// `consultaConTope` contesta al agotarse, ignora lo que llegue después y se puede cancelar al salir
// de la pantalla. Se reutiliza en vez de montar aquí otro temporizador — es el mismo criterio por el
// que `barreraRuta` existe, y dos mecanismos de espera distintos se acaban comportando distinto.
import { consultaConTope } from '../../js/barreraRuta.js';

/**
 * #331 · Cuánto espera ESTA pantalla antes de dar la consulta por fallida.
 *
 * Tiene que ser menor que los topes de arriba para que el mensaje lo dé la aplicación y no una
 * página de error: el presupuesto de tiempo del servidor son **60 s** (#265, que aplica a los GET y
 * no excluye las rutas de este panel: contesta un 503 con el código `PRESUPUESTO_AGOTADO`) y el
 * corte de la pasarela está en los **~90 s**. Con 30 s hay margen de sobra para los dos y sigue
 * siendo más de lo que este panel tarda cuando va bien —lee un materializado, no agrega—.
 *
 * El tope es del cliente, así que cubre también el caso que ningún tope del servidor cubre: que la
 * petición no vuelva nunca y no se ejecute ni el `then` ni el `catch`.
 */
const TOPE_CARGA_MS = 30000;

/**
 * Los estados con los que `DatabaseModel.sendRequest` cuenta un corte de tiempo: 524/522/520 de
 * Cloudflare, 504, 408, 598/599 y el `status 0` de la conexión cortada sin respuesta. Es SU lista,
 * copiada a propósito tal cual: la clasificación de qué es un corte la decide ese módulo, aquí solo
 * se traduce a lo que se le dice a quien está delante —su mensaje habla de reintentar una escritura
 * que puede haberse aplicado, y estas dos peticiones son lecturas—.
 */
const CORTES_DE_TIEMPO = [408, 504, 520, 522, 524, 598, 599];

/** El 503 del presupuesto de tiempo del servidor (#265), que trae su propio mensaje en español. */
const PRESUPUESTO_AGOTADO = 'PRESUPUESTO_AGOTADO';

function rejillaVacia() {
  return { totalImporte: 0, totalInformes: 0, numInspectores: 0, totalLineasDistintas: 0, inspectores: [] };
}

function detalleVacio() {
  return { kpis: {}, antiguedad: [], materias: [], items: [] };
}

/**
 * Qué se le cuenta a quien mira la pantalla: por qué no hay datos. Un «no se ha podido cargar» sin
 * motivo no deja decidir si hay que volver a pulsar, avisar a soporte o esperar.
 */
function motivoDelFallo(e) {
  const generico = 'El servidor no ha contestado como se esperaba.';
  if (!e) return generico;
  if (typeof e == 'string') return e;
  if (CORTES_DE_TIEMPO.indexOf(e.status) !== -1 || e.status === 0) {
    return 'La consulta ha tardado demasiado y se ha cortado antes de traer los datos.';
  }
  // Mensaje del servidor, que ya viene escrito para quien lo lee («La consulta ha superado el tiempo
  // máximo de 60 s y se ha cancelado…»). Se comprueba el código porque el 503 no está —ni tiene que
  // estar— en la lista de cortes de `sendRequest`, así que llega por la rama genérica.
  if (e.code === PRESUPUESTO_AGOTADO) {
    return e.message || 'La consulta ha superado el tiempo máximo que le da el servidor y se ha cancelado.';
  }
  return e.message || e.error || generico;
}

const COLORES_MATERIA = {
  REBT: '#1baf7a', REAT: '#2a78d6', RITE: '#eb6834', AE: '#eda100',
  IP: '#e87ba4', PCI: '#4a3aa7', SM: '#0ca30c', BT: '#6d5ce0', AT: '#1c5cab'
};
const PALETA = ['#4a3aa7', '#2a78d6', '#eb6834', '#1baf7a', '#eda100', '#e87ba4', '#6d5ce0', '#0ca30c'];

export default {
  components: { SelloDatos },
  data() {
    return {
      flagOff: false,
      rejillaActualizado: null,
      detalleActualizado: null,
      sel: '__TODOS__',
      // #331 · Las dos mitades de la pantalla llevan su estado por separado, porque llegan en dos
      // peticiones y una puede fallar mientras la otra trae datos buenos. Arrancan en `true`: la
      // pantalla pide las dos al montarse, y con `false` habría un instante en el que ni se está
      // cargando ni ha fallado nada, y ahí es cuando se pintan los ceros y los rótulos de vacío.
      cargando: { rejilla: true, detalle: true },
      // El motivo del fallo de cada mitad, o null. Nunca a la vez que su `cargando`.
      error: { rejilla: null, detalle: null },
      rejilla: rejillaVacia(),
      detalle: detalleVacio()
    };
  },
  computed: {
    // #16 · La pantalla enseña a la vez la rejilla de todos los inspectores y el detalle del
    // elegido, y cada una llega en su propia peticion: al pinchar un inspector solo se recarga el
    // detalle, asi que los dos pueden venir de materializados distintos. Se sella con el MAS
    // ANTIGUO de los dos, y con "no se sabe" si a alguno le falta la marca: es el unico criterio
    // que no le atribuye a lo que hay en pantalla una frescura que no tiene.
    actualizado() {
      var r = this.rejillaActualizado;
      var d = this.detalleActualizado;
      if (!r || !d) return null;
      return new Date(r) <= new Date(d) ? r : d;
    },
    kpis() { return this.detalle.kpis || {}; },
    antiguedad() { return this.detalle.antiguedad || []; },
    materias() { return this.detalle.materias || []; },
    items() { return this.detalle.items || []; },
    maxAging() { return Math.max(1, ...this.antiguedad.map(b => Number(b.importe) || 0)); },
    maxMateria() { return Math.max(1, ...this.materias.map(m => Number(m.importe) || 0)); }
  },
  created() {
    // Un carril por petición: decide él cuál de las respuestas en vuelo escribe en la pantalla, que
    // es lo que no se puede repetir a mano en cada `then` sin olvidarlo en alguno. Hace falta aquí
    // porque pulsar dos inspectores seguidos deja dos detalles en vuelo, y «Reintentar» lanza otro
    // sin que el anterior —al que se le agotó el tope— haya terminado: sin carril, el fallo del
    // viejo borraría los datos del nuevo y pintaría un error sobre cifras buenas.
    // Fuera de `data` porque no es estado que se pinte.
    //
    // #472 · Y el «cargando» de cada mitad lo enciende y lo apaga su carril, que es el único que sabe
    // cuándo ha contestado la petición vigente: una descartada no lo apaga —lo apaga la que la
    // sustituyó, que es la que sigue en vuelo— y no hay un `false` por manejador que olvidar.
    const self = this;
    const conSuCargando = function(parte) {
      return carrilPeticiones({ cargando: function(si) { self.cargando[parte] = si; } });
    };
    this.carriles = { rejilla: conSuCargando('rejilla'), detalle: conSuCargando('detalle') };
    // Los topes de tiempo VIVOS de cada mitad, para cancelarlos todos al salir de la pantalla.
    // Una lista y no un solo tope: pulsar tres inspectores seguidos deja tres detalles en vuelo
    // —el carril decide cuál pinta, pero los tres siguen pidiendo—, y guardando solo el último los
    // temporizadores de los anteriores sobrevivían a la salida hasta cumplir sus 30 s, retenido
    // con ellos el componente entero (revisión CODEX del PR #1819).
    this.topes = { rejilla: [], detalle: [] };
  },
  mounted() {
    this.cargar();
  },
  methods: {
    cargar() {
      var self = this;
      // El flag no gatea la pantalla: si su consulta falla no se dice nada, porque lo único que se
      // perdería es el aviso de «desactivada» y darla por apagada sería afirmar otra cosa sin saber.
      InformesPendientes.estado().then(function(res) {
        self.flagOff = res && res.habilitado === false;
      }).catch(function() {});
      this.cargarRejilla();
      this.cargarDetalle(this.sel);
    },
    cargarRejilla() {
      var self = this;
      self.pedirConTope('rejilla', function() { return InformesPendientes.rejilla(); }, {
        exito: function(res) {
          self.rejilla = self.normalizar(res) || rejillaVacia();
          self.rejillaActualizado = (res && res.actualizadoEn) || null;
        },
        limpiar: function() {
          // Con la carga fallida no queda nada que respalde las tarjetas, así que se vacían y es el
          // aviso el que ocupa su sitio: dejarlas puestas junto al «no se ha podido cargar» sería
          // decir las dos cosas a la vez. El sello de frescura se cae solo a «no se sabe».
          self.rejilla = rejillaVacia();
          self.rejillaActualizado = null;
        }
      });
    },
    cargarDetalle(inspector) {
      var self = this;
      self.pedirConTope('detalle', function() { return InformesPendientes.detalle(inspector); }, {
        // De qué inspector es esta carga, y qué se está mirando cuando conteste: la respuesta —o el
        // fallo— de un inspector no se escribe sobre otro ya elegido.
        clave: inspector,
        deAhora: function() { return self.sel; },
        exito: function(res) {
          self.detalle = self.normalizar(res) || detalleVacio();
          self.detalleActualizado = (res && res.actualizadoEn) || null;
        },
        limpiar: function() {
          // Lo que hubiera era de la selección anterior y de una respuesta que no ha llegado: ni se
          // enseña bajo el inspector nuevo, ni se suman sus cifras en los KPIs.
          self.detalle = detalleVacio();
          self.detalleActualizado = null;
        }
      });
    },
    /**
     * Pide una de las dos mitades de la pantalla con su tope de tiempo y su carril, y deja dicho en
     * cuál de los tres estados está: cargando, no se ha podido cargar —con el motivo y el botón de
     * reintentar— o el dato. Los tres se excluyen, y ninguno se queda puesto para siempre.
     */
    pedirConTope(parte, pedir, al) {
      const self = this;
      // Antes de pedir nada: «Reintentar» tiene que cambiar la pantalla al pulsarlo, y dejar el
      // «no se ha podido cargar» de la vez anterior mientras se vuelve a cargar es el mismo defecto
      // visto del revés. El «cargando» lo enciende el carril al lanzar (#472).
      self.error[parte] = null;
      const consulta = consultaConTope(pedir, TOPE_CARGA_MS);
      const vivos = self.topes[parte];
      vivos.push(consulta);
      const olvidarTope = function() {
        const i = vivos.indexOf(consulta);
        if (i !== -1) vivos.splice(i, 1);
      };
      // El tope deja de estar vivo cuando su petición termina, decida o no su respuesta lo que se
      // pinta: una descartada también ha apagado ya su temporizador. Se cuelga de la promesa y no
      // de los manejadores del carril porque es el único sitio por el que pasan los tres finales
      // —dato, fallo y descartada—; así la lista no crece mientras la pantalla está abierta.
      consulta.promesa.then(olvidarTope, olvidarTope);
      self.carriles[parte].lanzar(consulta.promesa, {
        clave: al.clave,
        deAhora: al.deAhora,
        exito: al.exito,
        // `limpiar` y `fallo` los llama el carril, en ese orden, solo cuando la que falla es la
        // respuesta que manda.
        limpiar: al.limpiar,
        fallo: function(e) {
          self.error[parte] = motivoDelFallo(e);
        }
      });
    },
    /**
     * Al salir de la pantalla no queda ningún tope vivo: duran 30 s y, sin cancelarlos, saltarían
     * después para escribir un error en una vista que ya no existe. Se cancelan TODOS los de cada
     * mitad, no solo el último lanzado: los que quedaron atrás —otro inspector, un «Reintentar»
     * sobre una petición que no había vuelto— siguen con su temporizador puesto.
     */
    cancelarTopes() {
      const topes = this.topes || {};
      Object.keys(topes).forEach(function(parte) {
        (topes[parte] || []).forEach(function(consulta) { consulta.cancelar(); });
        topes[parte] = [];
      });
    },
    // Jackson decapitaliza la secuencia inicial de mayúsculas del getter: getNInformes() sale como
    // "ninformes" y getNMas3Meses() como "nmas3Meses". Se toleran ambas grafías por si el backend
    // añade @JsonProperty algún día.
    normalizar(res) {
      if (!res) return res;
      if (res.inspectores) {
        res.inspectores.forEach(function(i) { if (i.nInformes == null) i.nInformes = i.ninformes || 0; });
      }
      var k = res.kpis;
      if (k) {
        if (k.nInformes == null) k.nInformes = k.ninformes || 0;
        if (k.nMas3Meses == null) k.nMas3Meses = k.nmas3Meses || 0;
      }
      return res;
    },
    // Client-side, mismo patrón que control-planificacion.vue: CSV con BOM y ";" (Excel es-ES lo abre
    // directo). Exporta el set cargado completo respetando el filtro por inspector (this.items).
    exportarExcel() {
      var esc = function(v) {
        var s = String(v == null ? '' : v);
        // Anti-inyección de fórmulas: Excel ejecuta como fórmula las celdas que empiezan por =,+,-,@
        // (o TAB/CR); el apóstrofo inicial las fuerza a texto.
        if (/^[=+\-@\t\r]/.test(s)) s = "'" + s;
        return '"' + s.replace(/"/g, '""') + '"';
      };
      var cab = ['Pedido', 'Linea', 'Expediente', 'Cliente', 'Materia', 'Inspector',
        'Fecha inspeccion', 'Antiguedad (dias)', 'Importe'];
      var cuerpo = this.items.map(function(it) {
        return [it.pedidoNumero, it.codigoLinea, it.codigoExpediente, it.cliente, it.materia, it.inspector,
          it.fechaInspeccion ? new Date(it.fechaInspeccion).toLocaleDateString('es-ES') : '', it.dias,
          String(it.importe == null ? '' : it.importe).replace('.', ',')].map(esc).join(';');
      });
      var csv = '﻿' + cab.join(';') + '\n' + cuerpo.join('\n');
      var blob = new Blob([csv], { type: 'text/csv;charset=utf-8' });
      var a = document.createElement('a');
      a.href = URL.createObjectURL(blob);
      a.download = 'informes-pendientes-' + (this.sel === '__TODOS__' ? 'todos' : this.sel) + '-'
        + new Date().toISOString().slice(0, 10) + '.csv';
      a.click();
      URL.revokeObjectURL(a.href);
    },
    seleccionar(id) {
      this.sel = id;
      this.cargarDetalle(id);
    },
    irAlPedido(pedidoId) {
      if (pedidoId) this.$f7router.navigate('/pedidos/view/' + pedidoId + '/');
    },
    barH(importe) {
      var v = Number(importe) || 0;
      // 0 € => sin barra (antes pintaba un muñón de 2% que parecía dato donde no lo había)
      return v <= 0 ? 0 : Math.max(2, v / this.maxAging * 100);
    },
    anchoMateria(importe) { return (Number(importe) || 0) / this.maxMateria * 100; },
    etiquetaX(b, i) {
      if (b.masDeDosAnios) return '>2a';
      if (b.mes != null && b.mes % 3 === 0) return this.mesCorto(b.mes) + ' ' + String(b.anio).slice(2);
      return '';
    },
    mesCorto(m) { return ['ene','feb','mar','abr','may','jun','jul','ago','sep','oct','nov','dic'][m] || ''; },
    claseEdad(dias) { return dias > 150 ? 'a4' : dias > 90 ? 'a3' : dias > 45 ? 'a2' : ''; },
    colorMateria(mat) {
      if (!mat) return '#8a8a8a';
      if (COLORES_MATERIA[mat]) return COLORES_MATERIA[mat];
      var h = 0; for (var i = 0; i < mat.length; i++) h = (h * 31 + mat.charCodeAt(i)) % PALETA.length;
      return PALETA[h];
    },
    eur(n) { return (Math.round(Number(n) || 0)).toLocaleString('es-ES') + ' €'; },
    fecha(d) { var x = new Date(d); return x.toLocaleDateString('es-ES'); },
    fechaHora(d) { var x = new Date(d); return x.toLocaleDateString('es-ES') + ' ' + x.toLocaleTimeString('es-ES', { hour: '2-digit', minute: '2-digit' }); }
  }
};
</script>

<style scoped>
.ipp { max-width: 1720px; margin: 0 auto; padding: 14px 20px 60px; color: #0b0b0b; }
.hero { border-radius: 16px; color: #fff; background: linear-gradient(135deg, #221a52, #4536a8 60%, #6d5ce0); padding: 18px 22px; margin-bottom: 12px; }
.hero .tag { font-size: 11px; background: rgba(255,255,255,.16); border: 1px solid rgba(255,255,255,.26); padding: 3px 10px; border-radius: 999px; }
.hero h1 { margin: 8px 0 2px; font-size: 20px; font-weight: 800; }
.hero .sub { opacity: .92; font-size: 13px; }
.hero .fresh { margin-top: 8px; font-size: 11.5px; background: rgba(255,255,255,.12); border: 1px solid rgba(255,255,255,.2); display: inline-block; padding: 3px 10px; border-radius: 999px; }
.banner { background: #fff8e6; border: 1px solid #f0d98a; border-radius: 10px; padding: 9px 13px; font-size: 12.5px; margin-bottom: 12px; }
.banner code { background: #f2ecd0; padding: 1px 5px; border-radius: 4px; }
.nota-importe { background: #eef3fb; border: 1px solid #c3d4ec; border-left: 4px solid #4536a8;
  border-radius: 10px; padding: 9px 13px; font-size: 12.5px; line-height: 1.45; color: #2c3550;
  margin-bottom: 12px; }
.sect { font-size: 11px; font-weight: 800; color: #898781; text-transform: uppercase; letter-spacing: .05em; margin: 6px 2px 8px; }
.grid { display: grid; grid-template-columns: repeat(auto-fill, minmax(196px, 1fr)); gap: 10px; margin-bottom: 16px; }
/* "ipchip"/"ipcard" y NO "chip"/"card": son clases globales de Framework7 (chip fija height 24px e
   inline-flex; card mete márgenes) y aplastaban la cabecera solapando las tarjetas. */
.ipchip { background: #fcfcfb; border: 1px solid #e1e0d9; border-radius: 12px; padding: 12px 14px; cursor: pointer; box-shadow: 0 1px 2px rgba(11,11,11,.04); transition: border-color .12s, box-shadow .12s; min-width: 0; }
.ipchip:hover { border-color: #6d5ce0; }
.ipchip.sel { border-color: #6d5ce0; box-shadow: 0 0 0 2px #efedfb; }
.ipchip .n { font-weight: 750; font-size: 14px; }
.ipchip .e { font-size: 19px; font-weight: 780; margin-top: 2px; font-variant-numeric: tabular-nums; }
.ipchip .m { font-size: 11.5px; color: #898781; margin-top: 1px; }
.ipchip .n, .ipchip .m { white-space: nowrap; overflow: hidden; text-overflow: ellipsis; }
.ipchip.total { background: linear-gradient(135deg, #efedfb, #f6f5fe); border-color: #ddd7f6; grid-column: span 2; }
.ipchip.total .n, .ipchip.total .e, .ipchip.sel .n { color: #4a3aa7; }
@media (max-width: 419px) { .ipchip.total { grid-column: auto; } }
.kpis { display: grid; grid-template-columns: repeat(4, 1fr); gap: 11px; margin: 2px 0 16px; }
.kpi { background: #fcfcfb; border: 1px solid #e1e0d9; border-radius: 14px; padding: 13px 15px; box-shadow: 0 1px 2px rgba(11,11,11,.04); }
.kpi .lab { font-size: 10.5px; color: #898781; font-weight: 700; text-transform: uppercase; }
.kpi .val { font-size: 22px; font-weight: 780; margin-top: 3px; font-variant-numeric: tabular-nums; }
.kpi .ksub { font-size: 11.5px; color: #52514e; margin-top: 2px; }
.kpi.alert .val { color: #ec835a; }
.row2 { display: grid; grid-template-columns: 1.5fr 1fr; gap: 14px; margin-bottom: 14px; }
.ipcard { background: #fcfcfb; border: 1px solid #e1e0d9; border-radius: 16px; box-shadow: 0 1px 2px rgba(11,11,11,.04); padding: 15px 17px; margin: 0; }
.ipcard h3 { margin: 0 0 2px; font-size: 14px; font-weight: 750; }
.ipcard .cs { font-size: 11.5px; color: #898781; margin-bottom: 12px; }
.cardhead { display: flex; justify-content: space-between; align-items: flex-start; gap: 12px; }
/* width:auto: vuetify.css (global) fuerza width:100% en los button (mismo roce que la toolbar del calendario) */
.expbtn { width: auto; flex: 0 0 auto; background: #fff; border: 1px solid #6d5ce0; color: #4a3aa7; font-weight: 700; font-size: 12px; border-radius: 8px; padding: 7px 13px; cursor: pointer; white-space: nowrap; }
.expbtn:hover { background: #efedfb; }
.expbtn:disabled { border-color: #d8d7cf; color: #a8a69e; cursor: default; background: #fff; }
.tblwrap { max-height: 62vh; overflow: auto; }
.tblwrap .tbl th { position: sticky; top: 0; background: #fcfcfb; z-index: 1; box-shadow: 0 1px 0 #e1e0d9; }
.plot { display: flex; align-items: flex-end; gap: 3px; height: 150px; border-bottom: 2px solid #c3c2b7; }
/* "ipcol" y NO "col": vuetify.css (importado en calendario.vue; los @import de un style scoped caen
   GLOBALES) estila el ".col" a pelo con width:100% + padding:12px → en columnas de ~25px el padding
   dejaba la barra en subpíxel y el gráfico salía "vacío". */
.ipcol { flex: 1; display: flex; align-items: flex-end; justify-content: center; height: 100%; }
.ipcol .f { width: 66%; background: #2a78d6; border-radius: 3px 3px 0 0; }
.ipcol:hover .f { background: #1c5cab; }
.ipcol.over2y .f { background: #d03b3b; }
.xlab { display: flex; gap: 3px; margin-top: 6px; }
.xlab div { flex: 1; text-align: center; font-size: 9.5px; color: #898781; white-space: nowrap; overflow: hidden; }
.mrow { display: grid; grid-template-columns: 64px 1fr auto; align-items: center; gap: 9px; margin: 9px 0; }
.mrow .ml { font-size: 12px; font-weight: 700; }
.mtrack { background: #eeede8; border-radius: 6px; height: 15px; overflow: hidden; }
.mfill { height: 100%; border-radius: 6px; }
.mrow .mv { font-size: 12px; font-weight: 700; font-variant-numeric: tabular-nums; text-align: right; min-width: 96px; }
.mrow .mv small { color: #898781; font-weight: 600; }
.swatch { display: inline-block; width: 9px; height: 9px; border-radius: 3px; margin-right: 5px; }
.tbl { width: 100%; border-collapse: collapse; font-size: 12.5px; }
.tbl th { text-align: left; font-size: 10px; text-transform: uppercase; color: #898781; font-weight: 700; padding: 8px 9px; border-bottom: 1px solid #e1e0d9; }
.tbl th.tr, .tbl td.tr { text-align: right; }
.tbl td { padding: 9px 9px; border-bottom: 1px solid #eeede8; font-variant-numeric: tabular-nums; }
.tbl td.txt { font-variant-numeric: normal; }
.tbl tr:hover td { background: #faf9ff; }
.ped { font-weight: 700; }
.b { font-weight: 700; }
.goped { color: #4a3aa7; font-weight: 700; white-space: nowrap; cursor: pointer; }
.goped:hover { text-decoration: underline; }
.mtag { font-size: 10px; font-weight: 800; padding: 2px 7px; border-radius: 999px; }
.itag { font-size: 10.5px; font-weight: 700; color: #52514e; background: #eeede8; padding: 2px 7px; border-radius: 999px; }
.age { font-weight: 700; }
.age.a2 { color: #e08b00; } .age.a3 { color: #ec835a; } .age.a4 { color: #d03b3b; }
.listmore { text-align: center; margin-top: 12px; font-size: 12px; color: #898781; }
.vacio { text-align: center; color: #898781; font-size: 12.5px; padding: 12px; }
/* #331 · "Se está cargando" y "no se ha podido cargar" ocupan el sitio del dato que falta y no
   mueven nada de lo que ya había: cuando la carga va bien no se pinta ninguno de los dos y la
   pantalla es la misma de siempre. Prefijo "ip" por lo mismo que .ipchip y .ipcard: las clases
   cortas chocan con las globales de Framework7 y de vuetify.css. Sin `gap` de flex, que el
   browserslist del repo (Chrome 49 / iOS 11) no tiene. */
.ipestado { display: flex; align-items: center; background: #fcfcfb; border: 1px solid #e1e0d9;
  border-radius: 14px; padding: 16px 18px; margin-bottom: 16px; color: #898781; font-size: 12.5px; }
.ipestado span { margin-left: 10px; }
.ipfallo { background: #fff4f4; border: 1px solid #f0c4c4; border-left: 4px solid #d03b3b;
  border-radius: 14px; padding: 14px 18px; margin-bottom: 16px; }
.ipfallo .t { font-size: 14px; font-weight: 750; color: #a52222; }
.ipfallo .m { font-size: 12.5px; color: #52514e; margin-top: 4px; line-height: 1.45; }
/* width:auto por lo mismo que .expbtn: vuetify.css (global) fuerza width:100% en los button. */
.ipbtn { width: auto; margin-top: 11px; background: #fff; border: 1px solid #d03b3b; color: #a52222;
  font-weight: 700; font-size: 12.5px; border-radius: 8px; padding: 7px 15px; cursor: pointer; }
.ipbtn:hover { background: #fdeaea; }
@media (max-width: 920px) { .row2 { grid-template-columns: 1fr; } .kpis { grid-template-columns: repeat(2, 1fr); } }

/* #16 · sello de "de cuando son estos datos", mismo sitio en todas las pantallas. */
.sello-datos-bar { padding: 6px 16px 0; }
</style>
